import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Spinner, Alert, Button } from 'reactstrap';
import { Document, Page, pdfjs } from 'react-pdf';
import { useResizeObserver } from '@wojtekmaj/react-hooks';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import FileViewer from 'react-file-viewer';
import { documentDownload } from 'store/documents/action';

// Hook up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const options = {
  cMapUrl: '/cmaps/',
  standardFontDataUrl: '/standard_fonts/',
};
const maxWidth = 800;
  
type FileInfo = { documentKey: string; documentExtension: string; documentName: string; };
type FilePreviewProps = { fileInfo: FileInfo; model: string; };

export default function FilePreview({ fileInfo, model }: FilePreviewProps) {
  const dispatch = useDispatch();
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);

  // initial zoom based on device pixel ratio
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const [scale, setScale] = useState<number>(dpr);

  // track container width
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
  const [width, setWidth] = useState(0);
  const onResize = useCallback<ResizeObserverCallback>(entries => {
    const entry = entries[0];
    if (entry) setWidth(entry.contentRect.width);
  }, []);
  useResizeObserver(containerRef, {}, onResize);

  // download blob
  useEffect(() => {
    let mounted = true;
    let objUrl: string | null = null;
  
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await dispatch(
          documentDownload(
            fileInfo.documentKey,
            model,
            async (response: any) => {
              if (!response.ok) throw new Error(response.statusText);
              const blob = await response.blob();
              objUrl = URL.createObjectURL(blob);
              if (mounted) setFileUrl(objUrl);
            },
            () => { throw new Error('Download failed'); }
          ) as any
        );
      } catch (e: any) {
        if (mounted) setError(e.message || 'Error loading file');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [fileInfo.documentKey, dispatch]);

  const fileProp = React.useMemo(() => fileUrl ? { url: fileUrl } : undefined, [fileUrl]);

  const style: React.CSSProperties = {
    width: '100%', height: '100%', overflow: 'auto', position: 'relative'
  };

  if (loading) return (
    <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner />
    </div>
  );
  if (error) return (
    <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
      <Alert color="danger">{error}</Alert>
      <Button onClick={() => window.location.reload()}>Retry</Button>
    </div>
  );
  if (!fileUrl) return null;

  // unified container for both PDF and other file types
  return (
    <div ref={setContainerRef} style={style}>
      {fileInfo.documentExtension.toLowerCase().replace(/^\./, '') === 'pdf' ? (
        <>
          <div className="d-flex gap-2 p-2 border-bottom">
            <Button size="sm" onClick={() => setScale(s => s + 0.25)}>Zoom +</Button>
            <Button size="sm" onClick={() => setScale(s => Math.max(dpr, s - 0.25))}>Zoom -</Button>
          </div>
          <div style={{ padding: 8 }}>
            <Document
              file={fileProp!}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={<Spinner />}
              error={<Alert color="danger">Error rendering PDF</Alert>}
              options={options}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={i}
                  pageNumber={i + 1}
                  width={Math.min(width, maxWidth)}
                  scale={scale}
                  renderTextLayer
                  renderAnnotationLayer
                />
              ))}
            </Document>
          </div>
        </>
      ) : (
        <div style={{ ...style }}>  {/* fallback uses full container width */}
          <FileViewer
            fileType={fileInfo.documentExtension.toLowerCase().replace(/^\./, '')}
            filePath={fileUrl}
            onError={(err: any) => {
              console.error('FileViewer error:', err);
              setError('Error displaying file');
            }}
          />
        </div>
      )}
    </div>
  );
}